<script setup>
    import { ref, computed } from "vue";
    import { Portal } from "portal-vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog, flashMessage } from "@/utils";
    import { useStore } from "@/store";
    import KButtonGroup from "@/components/ButtonGroup.vue";
    import KButton from "@/components/Button.vue";

    const store = useStore();

    const props = defineProps({
        params: {
            type: Object,
            required: false,
        },
    });

    const formRef = ref();

    const emit = defineEmits(["cancel", "created", "deleted"]);

    const isEditing = computed(() => {
        return !!props.params;
    });

    const fetchFormData = () => {
        const { params } = props;

        if (params) {
            return {
                sang: params.sang.id,
                startdatum: params.startdatum,
                starttid: params.starttid,
                slutdatum: params.slutdatum,
                sluttid: params.sluttid,
                orsak: params.orsak,
                kommentar: params.kommentar,
            };
        }

        return null;
    };

    const submit = async () => {
        const formData = await formRef.value?.manualSubmit();

        if (!formData) {
            return;
        }

        const payload = {
            sang: formData.sang,
            startdatum: formData.startdatum,
            starttid: formData.starttid,
            slutdatum: formData.slutdatum,
            sluttid: formData.sluttid,
            orsak: formData.orsak,
            kommentar: formData.kommentar,
            id: isEditing.value ? props.params.id : null,
        };

        try {
            if (isEditing.value) {
                await store.dispatch("vardplatsSparradTid/updateVardplatsSparradTid", {
                    vardplatsSparradTid: payload,
                });
            } else {
                await store.dispatch("vardplatsSparradTid/createVardplatsSparradTid", {
                    vardplatsSparradTid: payload,
                });
            }

            emit("created");
        } catch (e) {
            openDialog(`Kunde inte spärra tiden. ${getErrorMessage(e)}`, "error");
        }
    };

    const remove = async () => {
        await store.dispatch("vardplatsSparradTid/deleteVardplatsSparradTid", {
            vardplatsSparradTid: props.params,
        });

        flashMessage("Spärrad vårdplats borttagen");

        emit("deleted");
    };

    const cancel = () => {
        emit("cancel");
    };

    const fetchSangar = async () => {
        try {
            const sangarData = await klinikenApi.get("/slutenvard/sangar/", getConfig());

            return sangarData.data.results.map((sang) => ({
                value: sang.id,
                label: sang.namn,
            }));
        } catch (error) {
            openDialog(`Kunde inte ladda data. ${getErrorMessage(error)}`, "error");
        }
    };

    const fetchOrsaker = async () => {
        try {
            const orsakerData = await klinikenApi.get("/slutenvard/sparrningsorsaker", getConfig());

            return orsakerData.data.map((orsak) => ({
                value: orsak.id,
                label: orsak.namn,
            }));
        } catch (error) {
            openDialog(`Kunde inte ladda data. ${getErrorMessage(error)}`, "error");
        }
    };
</script>

<template>
    <Portal to="modal">
        <ModalContent
            :title="isEditing ? 'Redigera spärrad vårdplats' : 'Spärra vårdplats'"
            :showCloseButton="true"
            @close="cancel"
        >
            <span class="calendar-info">
                <img class="info-img" src="@/assets/info_dark.svg" alt="vårdplats-info" />
                Lägg in tidsperiod som inte är bokningsbar för vårdplats
            </span>

            <KForm
                ref="formRef"
                id="sparra-vardplats-form"
                class="sparra-vardplats-form"
                :initial-values-getter="fetchFormData"
            >
                <template #default>
                    <div class="form-content">
                        <KAutocomplete
                            label="Vårdplats"
                            name="sang"
                            :options="fetchSangar"
                            class="span-two"
                            validation="required"
                            validation-name="Vårdplats"
                        />
                        <FormulateInput
                            type="datepicker"
                            name="startdatum"
                            label="Startdatum *"
                            validation="required"
                            validation-name="Startdatum"
                        />

                        <FormulateInput
                            type="timepicker"
                            name="starttid"
                            label="Starttid * "
                            validation="required"
                            validation-name="Starttid"
                        />

                        <FormulateInput
                            type="datepicker"
                            name="slutdatum"
                            label="Slutdatum *"
                            validation="required"
                            validation-name="Slutdatum"
                        />

                        <FormulateInput
                            type="timepicker"
                            name="sluttid"
                            label="Sluttid *"
                            validation="required"
                            validation-name="Sluttid"
                        />

                        <KAutocomplete
                            :options="fetchOrsaker"
                            label="Orsak"
                            name="orsak"
                            class="span-two"
                            validation="required"
                            validation-name="Orsak"
                        />

                        <KText type="textarea" label="Kommentar" name="kommentar" class="span-two" />
                    </div>
                </template>
                <template #actions>
                    <KButtonGroup>
                        <KButton @click="submit">Spara spärrad tid</KButton>
                        <KButton v-if="isEditing" size="secondary" variant="alert" type="button" @click="remove">
                            Ta bort spärrad tid
                        </KButton>
                        <KButton size="secondary" variant="contour" type="button" @click="cancel">Avbryt</KButton>
                    </KButtonGroup>
                </template>
            </KForm>
        </ModalContent>
    </Portal>
</template>

<style lang="scss" scoped>
    .calendar-info {
        display: block;
        margin-bottom: 2rem;

        .info-img {
            margin-right: 0.5rem;
        }
    }
</style>
